import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stack } from '@mui/material';

import { AppDrawer, AppDrawerHeader } from '@common/ui/AppDrawer';
import { REQUEST_PARAM } from '@common/constants';

import { AccessStatus, useGetAccessRequest } from '@client-app-sdk';

import RevocationTimeCell from '@MyRequests/components/RevocationTimeCell';
import RequestTitle from '@MyRequests/components/RequestTitle';

import RevokeRequest from '@MyRequests/organisms/RevokeRequest';
import RequestAgain from '@MyRequests/organisms/RequestAgain';
import RequestContent from './RequestContent';
import { useFlagFixed } from '@common/utils/flags/use-flag-fixed';
import { Flags } from '@common/utils/flags/feature-flags';

const ACTIVITY_DRAWER_WIDTH = 750;

export default function RequestDrawer() {
  const [queryParams, setQueryParams] = useSearchParams();

  const requestId = useMemo(() => queryParams.get(REQUEST_PARAM), [queryParams]);

  const handleClose = () => {
    queryParams.delete(REQUEST_PARAM);
    setQueryParams(queryParams);
  };

  if (!requestId) {
    return null;
  }

  return <RenderRequestDrawer requestId={requestId} open={!!requestId} onClose={handleClose} />;
}

function RenderRequestDrawer({ requestId, open, onClose }: { requestId: string; open: boolean; onClose: () => void }) {
  const { data, isFetched, isFetching, error, refetch, dataUpdatedAt } = useGetAccessRequest(requestId, 10 * 1000);
  const { isEnabled: isMyAccessEnabled } = useFlagFixed(Flags.PORTAL_MY_ACCESS);
  const drawerHeader = useMemo(
    () => (
      <AppDrawerHeader
        title={<RequestTitle {...{ requestId, isFetching, dataUpdatedAt }} onFetch={refetch} />}
        onCloseClick={onClose}
      />
    ),
    [dataUpdatedAt, isFetching, onClose, refetch, requestId],
  );

  const drawerFooter = useMemo(() => {
    if (!data) return;

    switch (data.status.status) {
      case AccessStatus.Active:
      case AccessStatus.GRANTED:
      case AccessStatus.Pending:
      case AccessStatus.PENDING:
      case AccessStatus.Granting:
      case AccessStatus.APPROVED:
        return (
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
            <RevocationTimeCell revocation_time={data.revocation_time} />
            <RevokeRequest request={data} />
          </Stack>
        );
      case AccessStatus.Rejected:
      case AccessStatus.REJECTED:
      case AccessStatus.Revoked:
      case AccessStatus.EXPIRED:
      case AccessStatus.Failed:
      case AccessStatus.FAILED:
        return (
          <Stack alignItems="center">
            <RequestAgain request={data} />
          </Stack>
        );
    }
  }, [data]);

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      header={drawerHeader}
      footer={drawerFooter}
      width={ACTIVITY_DRAWER_WIDTH}
      variant="temporary"
      noBackdrop
    >
      <RequestContent {...{ data, error, isFetched, isMyAccessEnabled }} />
    </AppDrawer>
  );
}
